import React, { useMemo } from 'react';
import { BackgroundThemeRevision, ColorId } from 'referral-types';
import chroma from 'chroma-js';
import { rgbStringToHex } from '../../../../../utils/colorUtils';
import { useTheme } from '../../../../../context/theme/useTheme';
import { useBodyStyleVersion } from '../../../../../hooks/useBodyStyleVersion';
import { getCssVariableForColorId } from '../../../../../constants';

/*

the following background takes 2 color inputs: #880088 #FFFFFF

background-color: #880088;
background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1500 1000'%3E%3Cdefs%3E%3Cpath id='a' d='M1425-168v1200c0 150 12 202 81 270s418 376 418 376M1350-168v1200c0 150 11 199 72 270s371 377 371 377M1275-168v1200c0 150 9 195 63 270s325 379 325 379M1200-168v1200c0 150 8 192 54 270s278 380 278 380M1125-168v1200c0 150 7 188 45 270s232 382 232 382M1050-168v1200c0 150 5 184 36 270s186 384 186 384M975-168v1200c0 150 4 181 27 270s139 385 139 385M900-168v1200c0 150 3 177 18 270s93 387 93 387M825-168v1200c0 150 1 174 9 270s46 388 46 388' /%3E%3Cg fill='none' id='c' stroke-width='1.2'%3E%3Cpath d='M0 251h1500M0 331h1500M0 407h1500M0 478h1500M0 543h1500M0 603h1500M0 658h1500M0 708h1500M0 758h1500M0 813h1500M0 938h1500M0 873h1500M0 166h1500M0 76h1500' /%3E%3Cg transform='translate(0 -690)'%3E%3Cg transform='translate(0 0)'%3E%3Cuse href='%23a' /%3E%3Cpath d='M750 -168v1950h.2' /%3E%3Cuse href='%23a' transform='scale(-1 1)' style='transform-origin:50%25' /%3E%3C/g%3E%3C/g%3E%3C/g%3E%3ClinearGradient id='b' r='4000' x1='0' x2='0' y1='0' y2='1'%3E%3Cstop offset='0' stop-color='%23FFF'%3E%3Canimate attributeName='stop-color' dur='5s' repeatCount='999' begin='-1' values='%23880088%3B%23ffffff%3B%23880088%3B%23880088%3B%23880088' /%3E%3C/stop%3E%3Cstop offset='.2' stop-color='%23880088'%3E%3Canimate attributeName='stop-color' dur='5s' repeatCount='999' begin='-.8' values='%23880088%3B%23ffffff%3B%23880088%3B%23880088%3B%23880088' /%3E%3C/stop%3E%3Cstop offset='.4' stop-color='%23880088'%3E%3Canimate attributeName='stop-color' dur='5s' repeatCount='999' begin='-.6' values='%23880088%3B%23ffffff%3B%23880088%3B%23880088%3B%23880088' /%3E%3C/stop%3E%3Cstop offset='.6' stop-color='%23880088'%3E%3Canimate attributeName='stop-color' dur='5s' repeatCount='999' begin='-.4' values='%23880088%3B%23ffffff%3B%23880088%3B%23880088%3B%23880088' /%3E%3C/stop%3E%3Cstop offset='.8' stop-color='%23880088'%3E%3Canimate attributeName='stop-color' dur='5s' repeatCount='999' begin='-.2' values='%23880088%3B%23ffffff%3B%23880088%3B%23880088%3B%23880088' /%3E%3C/stop%3E%3Cstop offset='1' stop-color='%23880088'%3E%3Canimate attributeName='stop-color' dur='5s' repeatCount='999' begin='0' values='%23880088%3B%23ffffff%3B%23880088%3B%23880088%3B%23880088' /%3E%3C/stop%3E%3C/linearGradient%3E%3C/defs%3E%3Cuse href='%23c' stroke='url(%23b)' /%3E%3Cuse href='%23c' stroke='%23FFF' stroke-opacity='.2' /%3E%3C/svg%3E");
background-attachment: fixed;
background-size: cover;

*/

const ElectrifyingSlopeBackground: React.FC<{ themeRevision?: Partial<BackgroundThemeRevision> }> = (props) => {
  const { colorId1, colorId2 } = props.themeRevision || {};
  const { isDarkMode } = useTheme();
  const bodyStyleVersion = useBodyStyleVersion();

  const computedColors = useMemo(() => {
    const colorStyles1 = getCssVariableForColorId(colorId1 || ColorId.Primary2, isDarkMode);
    const colorStyles2 = getCssVariableForColorId(colorId2 || ColorId.Primary7, isDarkMode);
    const rootStyles = getComputedStyle(document.body);
    const c1 = rgbStringToHex(rootStyles.getPropertyValue(colorStyles1).trim() || '136 0 136');
    const c2 = rgbStringToHex(rootStyles.getPropertyValue(colorStyles2).trim() || '255 255 255');

    // Sort by luminance
    const colors = [chroma(c1), chroma(c2)];
    // colors.sort((a, b) => a.get('hsl.l') - b.get('hsl.l'));
    const darkColor = colors[0].hex();
    const brightColor = colors[1].hex();

    return { darkColor, brightColor };
  }, [colorId1, colorId2, isDarkMode, bodyStyleVersion]);

  // Perspective slope paths - curved lines converging toward center
  const slopePath =
    'M1425-168v1200c0 150 12 202 81 270s418 376 418 376M1350-168v1200c0 150 11 199 72 270s371 377 371 377M1275-168v1200c0 150 9 195 63 270s325 379 325 379M1200-168v1200c0 150 8 192 54 270s278 380 278 380M1125-168v1200c0 150 7 188 45 270s232 382 232 382M1050-168v1200c0 150 5 184 36 270s186 384 186 384M975-168v1200c0 150 4 181 27 270s139 385 139 385M900-168v1200c0 150 3 177 18 270s93 387 93 387M825-168v1200c0 150 1 174 9 270s46 388 46 388';

  // Horizontal lines at various Y positions
  const horizontalLines =
    'M0 251h1500M0 331h1500M0 407h1500M0 478h1500M0 543h1500M0 603h1500M0 658h1500M0 708h1500M0 758h1500M0 813h1500M0 938h1500M0 873h1500M0 166h1500M0 76h1500';

  const svgContent = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1500 1000'>
<defs>
<path id='a' d='${slopePath}' />
<g fill='none' id='c' stroke-width='1.2'>
<path d='${horizontalLines}' />
<g transform='translate(0 -690)'>
<g transform='translate(0 0)'>
<use href='#a' />
<path d='M750 -168v1950h.2' />
<use href='#a' transform='scale(-1 1)' style='transform-origin:50%' />
</g>
</g>
</g>
<linearGradient id='b' r='4000' x1='0' x2='0' y1='0' y2='1'>
<stop offset='0' stop-color='${computedColors.brightColor}'>
<animate attributeName='stop-color' dur='5s' repeatCount='indefinite' begin='-1' values='${computedColors.darkColor};${computedColors.brightColor};${computedColors.darkColor};${computedColors.darkColor};${computedColors.darkColor}' />
</stop>
<stop offset='.2' stop-color='${computedColors.darkColor}'>
<animate attributeName='stop-color' dur='5s' repeatCount='indefinite' begin='-.8' values='${computedColors.darkColor};${computedColors.brightColor};${computedColors.darkColor};${computedColors.darkColor};${computedColors.darkColor}' />
</stop>
<stop offset='.4' stop-color='${computedColors.darkColor}'>
<animate attributeName='stop-color' dur='5s' repeatCount='indefinite' begin='-.6' values='${computedColors.darkColor};${computedColors.brightColor};${computedColors.darkColor};${computedColors.darkColor};${computedColors.darkColor}' />
</stop>
<stop offset='.6' stop-color='${computedColors.darkColor}'>
<animate attributeName='stop-color' dur='5s' repeatCount='indefinite' begin='-.4' values='${computedColors.darkColor};${computedColors.brightColor};${computedColors.darkColor};${computedColors.darkColor};${computedColors.darkColor}' />
</stop>
<stop offset='.8' stop-color='${computedColors.darkColor}'>
<animate attributeName='stop-color' dur='5s' repeatCount='indefinite' begin='-.2' values='${computedColors.darkColor};${computedColors.brightColor};${computedColors.darkColor};${computedColors.darkColor};${computedColors.darkColor}' />
</stop>
<stop offset='1' stop-color='${computedColors.darkColor}'>
<animate attributeName='stop-color' dur='5s' repeatCount='indefinite' begin='0' values='${computedColors.darkColor};${computedColors.brightColor};${computedColors.darkColor};${computedColors.darkColor};${computedColors.darkColor}' />
</stop>
</linearGradient>
</defs>
<use href='#c' stroke='url(#b)' />
<use href='#c' stroke='${computedColors.brightColor}' stroke-opacity='.2' />
</svg>`;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        width: '100%',
        height: '100%',
        backgroundColor: computedColors.darkColor,
        backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(svgContent)}")`,
        backgroundAttachment: 'fixed',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        pointerEvents: 'none',
      }}
    />
  );
};

export default ElectrifyingSlopeBackground;
